import { createContext, useCallback, useContext, useLayoutEffect, useState } from 'react';

const THEME_KEY = 'bypass-theme';
const THEME_USER_SET_KEY = 'bypass-theme-user-set';

const ThemeContext = createContext(null);

function getSystemTheme() {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function isUserThemeSet() {
    return localStorage.getItem(THEME_USER_SET_KEY) === '1';
}

function getStoredTheme() {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return getSystemTheme();
}

function getInitialTheme() {
    if (isUserThemeSet()) {
        return getStoredTheme();
    }
    return getSystemTheme();
}

function applyTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
        meta.setAttribute('content', theme === 'dark' ? '#0c0c0b' : '#e6e4df');
    }
}

function persistTheme(theme) {
    localStorage.setItem(THEME_KEY, theme);
    localStorage.setItem(THEME_USER_SET_KEY, '1');
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(getInitialTheme);
    const [isUserSet, setIsUserSet] = useState(isUserThemeSet);

    useLayoutEffect(() => {
        applyTheme(theme);
    }, [theme]);

    useLayoutEffect(() => {
        const media = window.matchMedia('(prefers-color-scheme: dark)');

        const handleChange = () => {
            if (!isUserThemeSet()) {
                const systemTheme = media.matches ? 'dark' : 'light';
                setThemeState(systemTheme);
                setIsUserSet(false);
            }
        };

        media.addEventListener('change', handleChange);
        return () => media.removeEventListener('change', handleChange);
    }, []);

    const setTheme = useCallback((next) => {
        const value = next === 'dark' ? 'dark' : 'light';
        setThemeState(value);
        persistTheme(value);
        setIsUserSet(true);
    }, []);

    const toggleTheme = useCallback(() => {
        setThemeState((prev) => {
            const next = prev === 'dark' ? 'light' : 'dark';
            persistTheme(next);
            setIsUserSet(true);
            return next;
        });
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isUserSet }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error('useTheme must be used within ThemeProvider');
    }
    return ctx;
}
